'use client'

import { forwardRef, useState, useCallback, useEffect } from 'react'
import { Check, X, Eye, EyeOff, Bell, BellOff, Shield, ShieldOff, RotateCcw } from 'lucide-react'
import { safeAccess, safeArrayAccess } from '../utils/security'

/**
 * BIRHAUS Toggle Component
 * 
 * Enhanced toggle switch implementing all 10 BIRHAUS principles:
 * - Spanish-first labels and state descriptions
 * - Progressive disclosure of toggle state and effects
 * - Accessibility compliance (WCAG AA+)
 * - Undo functionality for critical settings
 * - Visual feedback with animations and state indicators
 * - IPU Admin specific presets for settings and permissions
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

interface ToggleState {
  on: string
  off: string
}

interface UndoState {
  previousValue: boolean
  timeout: NodeJS.Timeout | null
  isActive: boolean
}

export interface BirhausToggleProps {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // BIRHAUS Principle #4: Progressive disclosure
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // State labels
  onLabel?: string
  onLabelEs?: string
  onLabelEn?: string
  offLabel?: string
  offLabelEs?: string
  offLabelEn?: string
  
  // State descriptions
  onDescription?: string
  onDescriptionEs?: string
  onDescriptionEn?: string
  offDescription?: string
  offDescriptionEs?: string
  offDescriptionEn?: string
  
  // Value management
  checked?: boolean
  defaultChecked?: boolean
  onChange?: (checked: boolean) => void
  
  // BIRHAUS Principle #2: Honest data
  confirmationRequired?: boolean
  confirmationMessageEs?: string
  confirmationMessageEn?: string
  
  // BIRHAUS Principle #5: Undo over confirm
  enableUndo?: boolean
  undoTimeoutMs?: number
  criticalAction?: boolean // Shows longer undo timeout for critical settings
  
  // BIRHAUS Principle #6: Accessibility = dignity
  disabled?: boolean
  
  // Appearance
  variant?: 'default' | 'success' | 'warning' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  showStateLabels?: boolean
  showStateIcons?: boolean
  
  // Animation
  animationDuration?: number // in ms
  
  // Visual feedback
  loading?: boolean
  
  // Locale
  locale?: 'es' | 'en'
  
  // IPU Admin specific
  settingType?: 'privacy' | 'notifications' | 'permissions' | 'visibility' | 'security' | 'custom'
  
  // Async handling
  onAsyncChange?: (checked: boolean) => Promise<void>
}

export const BirhausToggle = forwardRef<HTMLButtonElement, BirhausToggleProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Description
  description,
  descriptionEs,
  descriptionEn,
  
  // State labels
  onLabel,
  onLabelEs,
  onLabelEn,
  offLabel,
  offLabelEs,
  offLabelEn,
  
  // State descriptions
  onDescription,
  onDescriptionEs,
  onDescriptionEn,
  offDescription,
  offDescriptionEs,
  offDescriptionEn,
  
  // Value
  checked,
  defaultChecked = false,
  onChange,
  
  // Confirmation
  confirmationRequired = false,
  confirmationMessageEs,
  confirmationMessageEn,
  
  // Undo
  enableUndo = false,
  undoTimeoutMs = 10000,
  criticalAction = false,
  
  // Basic props
  disabled = false,
  
  // Appearance
  variant = 'default',
  size = 'md',
  showStateLabels = true,
  showStateIcons = false,
  
  // Animation
  animationDuration = 200,
  
  // Visual feedback
  loading = false,
  
  // Locale
  locale = 'es',
  
  // IPU Admin
  settingType = 'custom',
  
  // Async
  onAsyncChange
}, ref) => {
  // State management
  const [internalChecked, setInternalChecked] = useState<boolean>(checked ?? defaultChecked)
  const [isAnimating, setIsAnimating] = useState(false)
  const [undoState, setUndoState] = useState<UndoState>({
    previousValue: false,
    timeout: null,
    isActive: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [pendingValue, setPendingValue] = useState<boolean | null>(null)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }, [locale])
  
  const displayLabel = getDisplayText(label, labelEs, labelEn, '')
  const displayDescription = getDisplayText(description, descriptionEs, descriptionEn, '')
  
  // Get default state labels based on setting type
  const getDefaultStateLabels = useCallback((): ToggleState => {
    const stateConfigs: Record<string, ToggleState> = {
      privacy: {
        on: locale === 'es' ? 'Privado' : 'Private',
        off: locale === 'es' ? 'Público' : 'Public'
      },
      notifications: {
        on: locale === 'es' ? 'Activado' : 'Enabled',
        off: locale === 'es' ? 'Desactivado' : 'Disabled'
      },
      permissions: {
        on: locale === 'es' ? 'Permitido' : 'Allowed',
        off: locale === 'es' ? 'Bloqueado' : 'Blocked'
      },
      visibility: {
        on: locale === 'es' ? 'Visible' : 'Visible',
        off: locale === 'es' ? 'Oculto' : 'Hidden'
      },
      security: {
        on: locale === 'es' ? 'Seguro' : 'Secure',
        off: locale === 'es' ? 'Básico' : 'Basic'
      },
      custom: {
        on: locale === 'es' ? 'Sí' : 'Yes',
        off: locale === 'es' ? 'No' : 'No'
      }
    }
    
    return safeAccess(stateConfigs, settingType, stateConfigs.custom)
  }, [settingType, locale])
  
  const defaultStateLabels = getDefaultStateLabels()
  
  const currentOnLabel = getDisplayText(onLabel, onLabelEs, onLabelEn, defaultStateLabels.on)
  const currentOffLabel = getDisplayText(offLabel, offLabelEs, offLabelEn, defaultStateLabels.off)
  
  const currentOnDescription = getDisplayText(onDescription, onDescriptionEs, onDescriptionEn, '')
  const currentOffDescription = getDisplayText(offDescription, offDescriptionEs, offDescriptionEn, '')
  
  const confirmationMessage = getDisplayText(
    undefined,
    confirmationMessageEs,
    confirmationMessageEn,
    locale === 'es' 
      ? '¿Está seguro de cambiar esta configuración?'
      : 'Are you sure you want to change this setting?'
  )
  
  // Get state icon
  const getStateIcon = useCallback((isOn: boolean) => {
    const iconConfigs: Record<string, { on: React.ReactNode; off: React.ReactNode }> = {
      privacy: {
        on: <EyeOff className="h-4 w-4" />,
        off: <Eye className="h-4 w-4" />
      },
      notifications: {
        on: <Bell className="h-4 w-4" />,
        off: <BellOff className="h-4 w-4" />
      },
      permissions: {
        on: <Shield className="h-4 w-4" />,
        off: <ShieldOff className="h-4 w-4" />
      },
      visibility: {
        on: <Eye className="h-4 w-4" />,
        off: <EyeOff className="h-4 w-4" />
      },
      security: {
        on: <Shield className="h-4 w-4" />,
        off: <ShieldOff className="h-4 w-4" />
      },
      custom: {
        on: <Check className="h-4 w-4" />,
        off: <X className="h-4 w-4" />
      }
    }
    
    const config = safeAccess(iconConfigs, settingType, iconConfigs.custom)
    return isOn ? config.on : config.off
  }, [settingType])
  
  // Handle undo functionality
  const handleUndo = useCallback(() => {
    if (undoState.isActive && undoState.timeout) {
      clearTimeout(undoState.timeout)
      setInternalChecked(undoState.previousValue)
      onChange?.(undoState.previousValue)
      setUndoState({ previousValue: false, timeout: null, isActive: false })
    }
  }, [undoState, onChange])
  
  // Setup undo for changes
  const setupUndo = useCallback((newValue: boolean, oldValue: boolean) => {
    if (!enableUndo) return
    
    // Clear existing timeout
    if (undoState.timeout) {
      clearTimeout(undoState.timeout)
    }
    
    // Set up new undo state
    const timeoutDuration = criticalAction ? undoTimeoutMs * 2 : undoTimeoutMs
    const timeout = setTimeout(() => {
      setUndoState(prev => ({ ...prev, isActive: false, timeout: null }))
    }, timeoutDuration)
    
    setUndoState({
      previousValue: oldValue,
      timeout,
      isActive: true
    })
  }, [enableUndo, undoTimeoutMs, criticalAction, undoState.timeout])
  
  // Handle confirmation
  const handleConfirmation = useCallback((value: boolean) => {
    setShowConfirmation(false)
    setPendingValue(null)
    
    const oldValue = internalChecked
    setInternalChecked(value)
    
    // Setup undo
    setupUndo(value, oldValue)
    
    // Handle async change
    if (onAsyncChange) {
      setIsLoading(true)
      onAsyncChange(value)
        .finally(() => setIsLoading(false))
    }
    
    onChange?.(value)
  }, [internalChecked, setupUndo, onAsyncChange, onChange])
  
  // Handle toggle
  const handleToggle = useCallback(() => {
    if (disabled || isLoading) return
    
    const newValue = !internalChecked
    
    // Show confirmation if required
    if (confirmationRequired) {
      setPendingValue(newValue)
      setShowConfirmation(true)
      return
    }
    
    // Trigger animation
    setIsAnimating(true)
    setTimeout(() => setIsAnimating(false), animationDuration)
    
    const oldValue = internalChecked
    setInternalChecked(newValue)
    
    // Setup undo
    setupUndo(newValue, oldValue)
    
    // Handle async change
    if (onAsyncChange) {
      setIsLoading(true)
      onAsyncChange(newValue)
        .finally(() => setIsLoading(false))
    }
    
    onChange?.(newValue)
  }, [
    disabled,
    isLoading,
    internalChecked,
    confirmationRequired,
    animationDuration,
    setupUndo,
    onAsyncChange,
    onChange
  ])
  
  // Sync external value changes
  useEffect(() => {
    if (checked !== undefined && checked !== internalChecked) {
      setInternalChecked(checked)
    }
  }, [checked, internalChecked])
  
  // Cleanup undo timeout on unmount
  useEffect(() => {
    return () => {
      if (undoState.timeout) {
        clearTimeout(undoState.timeout)
      }
    }
  }, [undoState.timeout])
  
  // Style classes
  const sizeClasses = {
    sm: {
      toggle: 'w-8 h-4',
      thumb: 'w-3 h-3',
      text: 'text-sm',
      description: 'text-xs'
    },
    md: {
      toggle: 'w-11 h-6',
      thumb: 'w-5 h-5',
      text: 'text-base',
      description: 'text-sm'
    },
    lg: {
      toggle: 'w-14 h-8',
      thumb: 'w-6 h-6',
      text: 'text-lg',
      description: 'text-base'
    }
  }
  
  const variantClasses = {
    default: {
      on: 'bg-primary-600',
      off: 'bg-gray-200'
    },
    success: {
      on: 'bg-green-600',
      off: 'bg-gray-200'
    },
    warning: {
      on: 'bg-yellow-600',
      off: 'bg-gray-200'
    },
    danger: {
      on: 'bg-red-600',
      off: 'bg-gray-200'
    }
  }
  
  const currentSizeClasses = safeAccess(sizeClasses, size, sizeClasses.md)
  const currentVariantClasses = safeAccess(variantClasses, variant, variantClasses.default)
  
  const currentStateLabel = internalChecked ? currentOnLabel : currentOffLabel
  const currentStateDescription = internalChecked ? currentOnDescription : currentOffDescription
  
  return (
    <div className="birhaus-toggle">
      {/* Main Toggle Container */}
      <div className="flex items-start space-x-3">
        {/* Toggle Switch */}
        <button
          ref={ref}
          type="button"
          role="switch"
          aria-checked={internalChecked}
          aria-labelledby={displayLabel ? 'toggle-label' : undefined}
          aria-describedby={displayDescription ? 'toggle-description' : undefined}
          onClick={handleToggle}
          disabled={disabled || isLoading}
          className={cn(
            'relative inline-flex items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
            currentSizeClasses.toggle,
            internalChecked ? currentVariantClasses.on : currentVariantClasses.off,
            (disabled || isLoading) && 'opacity-50 cursor-not-allowed',
            !disabled && !isLoading && 'cursor-pointer',
            isAnimating && 'scale-95'
          )}
        >
          {/* Thumb */}
          <span
            className={cn(
              'inline-block rounded-full bg-white shadow-sm transform transition-transform duration-200',
              currentSizeClasses.thumb,
              internalChecked 
                ? size === 'sm' ? 'translate-x-4' : size === 'lg' ? 'translate-x-6' : 'translate-x-5'
                : 'translate-x-0.5'
            )}
          >
            {/* Loading Spinner */}
            {(isLoading || loading) && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-primary-600" />
              </div>
            )}
            
            {/* State Icon */}
            {showStateIcons && !isLoading && !loading && (
              <div className="absolute inset-0 flex items-center justify-center text-gray-600">
                {getStateIcon(internalChecked)}
              </div>
            )}
          </span>
        </button>
        
        {/* Labels and Description */}
        <div className="flex-1">
          {/* Main Label */}
          {displayLabel && (
            <div id="toggle-label" className={cn('font-medium', currentSizeClasses.text)}>
              {displayLabel}
              {criticalAction && (
                <span className="ml-2 text-xs text-orange-600 font-normal">
                  ({locale === 'es' ? 'Crítico' : 'Critical'})
                </span>
              )}
            </div>
          )}
          
          {/* State Label */}
          {showStateLabels && (
            <div className={cn(
              'font-medium mt-1',
              currentSizeClasses.description,
              internalChecked ? 'text-green-700' : 'text-gray-600'
            )}>
              {currentStateLabel}
            </div>
          )}
          
          {/* Main Description */}
          {displayDescription && (
            <p id="toggle-description" className={cn(
              'text-gray-600 mt-1',
              currentSizeClasses.description
            )}>
              {displayDescription}
            </p>
          )}
          
          {/* State Description */}
          {currentStateDescription && (
            <p className={cn(
              'text-gray-500 mt-1',
              currentSizeClasses.description
            )}>
              {currentStateDescription}
            </p>
          )}
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      {showConfirmation && pendingValue !== null && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className={cn('text-yellow-800 mb-3', currentSizeClasses.description)}>
            {confirmationMessage}
          </p>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => handleConfirmation(pendingValue)}
              className={cn(
                'px-3 py-1 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors',
                currentSizeClasses.description
              )}
            >
              {locale === 'es' ? 'Confirmar' : 'Confirm'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowConfirmation(false)
                setPendingValue(null)
              }}
              className={cn(
                'px-3 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors',
                currentSizeClasses.description
              )}
            >
              {locale === 'es' ? 'Cancelar' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
      
      {/* Undo Notification */}
      {undoState.isActive && (
        <div className="mt-3 bg-gray-800 text-white text-sm rounded-md p-3 flex items-center justify-between">
          <span>
            {locale === 'es' 
              ? `Configuración cambiada a "${currentStateLabel}"`
              : `Setting changed to "${currentStateLabel}"`
            }
            {criticalAction && (
              <span className="ml-2 text-orange-300">
                ({locale === 'es' ? 'Configuración crítica' : 'Critical setting'})
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={handleUndo}
            className="flex items-center space-x-1 text-primary-200 hover:text-primary-100 font-medium"
          >
            <RotateCcw className="h-3 w-3" />
            <span>{locale === 'es' ? 'Deshacer' : 'Undo'}</span>
          </button>
        </div>
      )}
    </div>
  )
})

BirhausToggle.displayName = 'BirhausToggle'

// Hook for enhanced toggle functionality
export const useBirhausToggle = (initialValue: boolean = false) => {
  const [isOn, setIsOn] = useState(initialValue)
  const [history, setHistory] = useState<boolean[]>([initialValue])
  const [historyIndex, setHistoryIndex] = useState(0)
  
  const toggle = useCallback(() => {
    const newValue = !isOn
    setIsOn(newValue)
    
    // Add to history
    const newHistory = history.slice(0, historyIndex + 1)
    newHistory.push(newValue)
    setHistory(newHistory.slice(-10)) // Keep last 10 states
    setHistoryIndex(newHistory.length - 1)
  }, [isOn, history, historyIndex])
  
  const turnOn = useCallback(() => {
    if (!isOn) toggle()
  }, [isOn, toggle])
  
  const turnOff = useCallback(() => {
    if (isOn) toggle()
  }, [isOn, toggle])
  
  const setValue = useCallback((value: boolean) => {
    if (value !== isOn) {
      setIsOn(value)
      
      // Add to history
      const newHistory = history.slice(0, historyIndex + 1)
      newHistory.push(value)
      setHistory(newHistory.slice(-10))
      setHistoryIndex(newHistory.length - 1)
    }
  }, [isOn, history, historyIndex])
  
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1
      setHistoryIndex(newIndex)
      const value = safeArrayAccess(history, newIndex, false)
      setIsOn(value)
      return value
    }
    return null
  }, [history, historyIndex])
  
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1
      setHistoryIndex(newIndex)
      const value = safeArrayAccess(history, newIndex, false)
      setIsOn(value)
      return value
    }
    return null
  }, [history, historyIndex])
  
  const canUndo = historyIndex > 0
  const canRedo = historyIndex < history.length - 1
  
  return {
    isOn,
    toggle,
    turnOn,
    turnOff,
    setValue,
    undo,
    redo,
    canUndo,
    canRedo
  }
}

// IPU Admin specific toggle configurations
export const ipuAdminTogglePresets = {
  memberVisibility: {
    settingType: 'visibility' as const,
    onLabelEs: 'Visible en directorio',
    onLabelEn: 'Visible in directory',
    offLabelEs: 'Oculto del directorio',
    offLabelEn: 'Hidden from directory',
    showStateLabels: true,
    enableUndo: true
  },
  
  donationNotifications: {
    settingType: 'notifications' as const,
    onLabelEs: 'Notificaciones activadas',
    onLabelEn: 'Notifications enabled',
    offLabelEs: 'Notificaciones desactivadas',
    offLabelEn: 'Notifications disabled',
    showStateIcons: true,
    enableUndo: true
  },
  
  dataPrivacy: {
    settingType: 'privacy' as const,
    variant: 'danger' as const,
    onLabelEs: 'Datos privados',
    onLabelEn: 'Private data',
    offLabelEs: 'Datos públicos',
    offLabelEn: 'Public data',
    confirmationRequired: true,
    criticalAction: true,
    enableUndo: true
  },
  
  adminPermissions: {
    settingType: 'permissions' as const,
    variant: 'warning' as const,
    onLabelEs: 'Permisos de administrador',
    onLabelEn: 'Administrator permissions',
    offLabelEs: 'Permisos básicos',
    offLabelEn: 'Basic permissions',
    confirmationRequired: true,
    criticalAction: true,
    enableUndo: true,
    showStateIcons: true
  },
  
  securityMode: {
    settingType: 'security' as const,
    variant: 'success' as const,
    onLabelEs: 'Modo seguro activado',
    onLabelEn: 'Secure mode enabled',
    offLabelEs: 'Modo básico',
    offLabelEn: 'Basic mode',
    showStateLabels: true,
    showStateIcons: true,
    enableUndo: true
  }
}